import {
  cellToChildrenSize,
  cellToLatLng,
  getResolution,
  polygonToCells,
} from 'h3-js';
import { GAMEPLAY } from '@/config/gameplay';
import type { CellId, CompactLoopInterior, DetectedLoop } from '@/types';
import { floodFillInterior, LoopTooLargeError } from './loops';
import { childrenOf, parentOf, ringOf } from './neighbours';

/**
 * Ekkora becsült jelöltrégió alatt a régi, teljes res12 Set gyorsabb és
 * egyszerűbb. Fölötte a hurok belsejét tömören tartjuk.
 */
const COMPACT_ABOVE_CELLS = 40_000;

/**
 * A compact parent két szinttel durvább a játékrácsnál: res12 → res10.
 * Egy teljes parent 49 res12 cellát képvisel. A res10 elég finom ahhoz, hogy
 * a fal körüli pontos határsáv keskeny maradjon, de már nagyságrenddel
 * csökkenti a memóriaigényt.
 */
const COMPACT_PARENT_STEPS = 2;

/**
 * Védőkorlát a TÖMÖR eredményre.
 *
 * 250 000 res10 parent legfeljebb ~12,25 millió res12 cellát (~3 760 km²)
 * képvisel. Ez már egy kb. 200 km kerületű kör teljes területének nagyságrendje,
 * miközben maga a compact halmaz még kezelhető méretű. Ennél nagyobb geometriát
 * továbbra is GPS-/importhibának tekintünk.
 */
const MAX_COMPACT_PARENTS = 250_000;

export interface LoopInteriorGeometry {
  /** Pontos res12 belső cellák a határsávban, vagy kis huroknál a teljes belső. */
  interior: Set<CellId>;
  /** Nagy huroknál a teljesen belső parentek tömör halmaza. */
  compactInterior?: CompactLoopInterior;
  /** A teljes belső pontos res12-egyenértékű cellaszáma. */
  cellCount: number;
}

/**
 * A hurok belsejének felépítése úgy, hogy nagy területnél ne materializáljunk
 * több millió res12 H3 stringet.
 */
export function buildLoopInterior(wall: ReadonlySet<CellId>): LoopInteriorGeometry {
  if (wall.size === 0) return { interior: new Set(), cellCount: 0 };

  if (estimateRegionCells(wall) <= COMPACT_ABOVE_CELLS) {
    const interior = floodFillInterior(wall);
    return { interior, cellCount: interior.size };
  }

  return buildCompactAdaptiveInterior(wall);
}

/** A teljes belső cellaszáma, reprezentációtól függetlenül. */
export function loopInteriorCellCount(loop: DetectedLoop): number {
  return loop.compactInterior?.cellCount ?? loop.interior.size;
}

/** Igaz, ha a res12 cella a hurok belsejében van. */
export function loopInteriorHas(loop: DetectedLoop, cell: CellId): boolean {
  if (loop.interior.has(cell)) return true;
  const compact = loop.compactInterior;
  if (!compact) return false;
  return compact.fullParents.has(parentOf(cell, compact.parentResolution));
}

/** A fal + belső teljes, res12-egyenértékű cellaszáma. */
export function loopCellCount(loop: DetectedLoop): number {
  // A flood fill definíció szerint a fal és a belső diszjunkt.
  return loop.wall.size + loopInteriorCellCount(loop);
}

export function hasCompactInterior(loop: DetectedLoop): boolean {
  return (loop.compactInterior?.fullParents.size ?? 0) > 0;
}

/**
 * Két hurok belsejének pontos metszetszáma a jelenlegi compact formában.
 *
 * A compact geometriák ugyanazon parentResolutionön készülnek. A kód az
 * explicit határsáv ↔ teljes parent keresztezéseket is beleszámolja, ezért a
 * deduplikáció nem csak közelítés.
 */
export function loopInteriorOverlapCount(a: DetectedLoop, b: DetectedLoop): number {
  const aCompact = a.compactInterior;
  const bCompact = b.compactInterior;

  if (!aCompact && !bCompact) {
    const smaller = a.interior.size <= b.interior.size ? a.interior : b.interior;
    const larger = smaller === a.interior ? b.interior : a.interior;
    let shared = 0;
    for (const cell of smaller) if (larger.has(cell)) shared += 1;
    return shared;
  }

  // Explicit res12 cellák: mindkét irányt külön számoljuk, mert az egyik
  // hurok határsávja a másik teljes parentjébe eshet.
  let shared = 0;
  const explicitSeen = new Set<CellId>();
  for (const cell of a.interior) {
    if (loopInteriorHas(b, cell)) {
      explicitSeen.add(cell);
      shared += 1;
    }
  }
  for (const cell of b.interior) {
    if (!explicitSeen.has(cell) && loopInteriorHas(a, cell)) shared += 1;
  }

  if (!aCompact || !bCompact) return shared;

  if (aCompact.parentResolution !== bCompact.parentResolution) {
    // Jelenleg ez nem fordulhat elő (minden compact belső ugyanazzal a
    // konstanssal készül). Inkább biztonságosan alulbecsüljük a parent-parent
    // részt, mint hogy több millió gyereket bontsunk ki egy ritka hibás állapotban.
    return shared;
  }

  const smallerParents =
    aCompact.fullParents.size <= bCompact.fullParents.size
      ? aCompact.fullParents
      : bCompact.fullParents;
  const largerParents =
    smallerParents === aCompact.fullParents ? bCompact.fullParents : aCompact.fullParents;

  for (const parent of smallerParents) {
    if (largerParents.has(parent)) {
      shared += Number(cellToChildrenSize(parent, GAMEPLAY.H3_RESOLUTION));
    }
  }
  return shared;
}

/**
 * Csak akkor használd, ha ténylegesen szükség van a finom cellákra.
 * Compact parentenként, streamelve bont vissza, tehát nem hoz létre egy újabb
 * többmilliós Setet, de természetesen maga a végigiterálás továbbra is O(n).
 */
export function* iterateLoopInteriorCells(loop: DetectedLoop): IterableIterator<CellId> {
  for (const cell of loop.interior) yield cell;
  const compact = loop.compactInterior;
  if (!compact) return;
  for (const parent of compact.fullParents) {
    for (const child of childrenOf(parent, GAMEPLAY.H3_RESOLUTION)) yield child;
  }
}

export function* iterateLoopCells(loop: DetectedLoop): IterableIterator<CellId> {
  for (const cell of loop.wall) yield cell;
  yield* iterateLoopInteriorCells(loop);
}

/* ════════════════════════════════════════════════════════════════════════
   Bővített fal belseje, kitöltés nélkül
   ════════════════════════════════════════════════════════════════════════ */

/**
 * Egy nagy, már felépített hurok (`base`) falát TARTALMAZÓ fal belső cellaszáma
 * — a belső újrafelépítése nélkül, ha az bizonyíthatóan csak zsugorodott.
 *
 * Tipikus eset a „nyeles kör”: a kör bezárása után az odaúton jössz haza, és a
 * hazaút minden lépése egy jelöltet ad, aminek a fala = a kör + az út két
 * oldala. Mérve (GRUNDO #60, `7d243470`, 130 km²): 615 ilyen jelölt, egyenként
 * ~250 ms teljes belső-felépítés, összesen 167 s a 179-ből — mind ugyanazon a
 * duplikátum-szűrőn akadt fenn.
 *
 * MIÉRT PONTOS. Legyen W ⊇ base.wall, E = W \ base.wall, F = base.wall ∪ belső.
 * A W komplementerének korlátos komponensei (= az új belső):
 *   - a régi belső E-n kívüli része (a régi fal továbbra is körbezárja), és
 *   - F ∪ E „lyukai”: a régi külsőből az E által újonnan bekerített zsebek.
 * F-nek nincs lyuka (a külső komplementere). Az új belső tehát PONTOSAN a régi
 * belső mínusz E, plusz F ∪ E lyukai.
 *
 * 1. A lyukak SZÁMA H3-rácson helyben követhető: egy cella hozzáadásakor a hat
 *    szomszédja közül a halmazba tartozók k darab összefüggő ívet alkotnak; ha
 *    ezek d különböző komponensbe tartoznak, a lyukak száma k − d-vel nő (teli
 *    gyűrűnél eggyel csökken). A hatszögrácson a cella és a komplementer
 *    szomszédsága ugyanaz, ezért ez a szokásos Euler-karakterisztika
 *    (lyuk = komponens − χ). F-et egy csomópontnak vesszük — ez csak akkor
 *    pontos, ha F összefüggő, ezért a régi falnak összefüggőnek kell lennie.
 * 2. A lyukak MÉRETÉT helyi kitöltéssel mérjük az E melletti szabad cellákból.
 *    Egy véges, teljesen bejárt komponens biztosan lyuk. Egy `POCKET_SCAN_CAP`
 *    fölé nőtt (vagy egy ilyenhez érő) komponenst nagynak jelölünk. Ha a véges
 *    komponensek száma megegyezik az 1. pont szerinti lyukszámmal, a nagyok
 *    mind a külsőhöz tartoznak — minden lyukat megtaláltunk és megszámoltunk.
 *
 * Tipikus lyuk: az odaút és a hazaút közé szorult néhány útcella.
 *
 * `null`, ha nem bizonyítható (a hívó ekkor a teljes felépítést futtatja): nem
 * compact alap, nem részhalmaz vagy nem összefüggő a régi fal, a jelölt a
 * régiókorlátot elérné, pentagon, vagy egy lyuk a keresési korlátnál nagyobb.
 */
export function shrunkInteriorCellCount(
  base: DetectedLoop,
  wall: ReadonlySet<CellId>,
): ShrunkInterior | null {
  const compact = base.compactInterior;
  if (!compact) return null;
  const first = wall.values().next().value as CellId | undefined;
  if (first === undefined) return null;
  const res = getResolution(first);
  const parentResolution = Math.max(0, res - COMPACT_PARENT_STEPS);
  if (parentResolution !== compact.parentResolution) return null;

  // E = az új falcellák. A régi fal részhalmaz ⇔ a maradék pontosan a régi fal.
  const extra = new Set<CellId>();
  for (const cell of wall) if (!base.wall.has(cell)) extra.add(cell);
  if (wall.size - extra.size !== base.wall.size) return null;

  const info = baseWallInfo(base.wall, parentResolution);
  if (!info.connected) return null;

  // Ugyanaz a döntés, amit a `buildLoopInterior()` hozna: compact út, és a
  // durva régió nem haladja meg a korlátot (különben ott `too_large` lenne).
  // A befoglaló doboz min/max, tehát a régi fal dobozából + E dobozából PONTOSAN
  // ugyanaz jön ki, mint az egész falra.
  const fine = unionBounds(info.bounds, boundsOf(extra));
  if (estimateCellCount(fine.south, fine.north, fine.west, fine.east, res) <= COMPACT_ABOVE_CELLS) {
    return null;
  }
  const extraParents = new Set<CellId>();
  for (const cell of extra) extraParents.add(parentOf(cell, parentResolution));
  const coarse = unionBounds(info.coarseBounds, boundsOf(extraParents));
  const estimated = estimateCellCount(coarse.south, coarse.north, coarse.west, coarse.east, parentResolution);
  if (estimated > GAMEPLAY.MAX_LOOP_BBOX_CELLS) return null;

  const inBase = (cell: CellId) => base.wall.has(cell) || loopInteriorHas(base, cell);
  const added = new Set<CellId>();
  /** Union-find az E-cellákon; F egyetlen őrcsomópont. */
  const BASE_ROOT = '';
  const root = new Map<CellId, CellId>([[BASE_ROOT, BASE_ROOT]]);
  const find = (cell: CellId): CellId => {
    if (inBase(cell)) return BASE_ROOT;
    let r = cell;
    while (root.get(r) !== r) r = root.get(r)!;
    let c = cell;
    while (c !== r) {
      const next = root.get(c)!;
      root.set(c, r);
      c = next;
    }
    return r;
  };

  let removed = 0;
  let holes = 0;
  for (const cell of extra) {
    if (loopInteriorHas(base, cell)) {
      // A régi belsőből falcella lett: a belső egy cellával kisebb, F nem változik.
      removed += 1;
      continue;
    }

    const ring = ringOf(cell);
    if (ring.length !== 7) return null; // pentagon: nem bizonyítunk
    const members = ring.filter((near) => near !== cell && (added.has(near) || inBase(near)));

    // A halmazba tartozó szomszédok összefüggő ívei (a hat szomszéd egy 6-kört alkot).
    const runRoots = new Set<CellId>();
    let runs = 0;
    const seen = new Set<CellId>();
    for (const start of members) {
      if (seen.has(start)) continue;
      runs += 1;
      const stack = [start];
      seen.add(start);
      while (stack.length > 0) {
        const at = stack.pop()!;
        for (const next of members) {
          if (!seen.has(next) && ringOf(at).includes(next)) {
            seen.add(next);
            stack.push(next);
          }
        }
      }
      runRoots.add(find(start));
    }

    if (members.length === 6) holes -= 1;
    else holes += runs - runRoots.size;

    // Összevonás: ha bármelyik ív F-hez tartozik, az egész komponens F lesz.
    const target = runRoots.has(BASE_ROOT) ? BASE_ROOT : cell;
    added.add(cell);
    root.set(cell, target);
    for (const r of runRoots) if (r !== BASE_ROOT) root.set(r, target);
  }

  const sharedWithBase = compact.cellCount - removed;
  if (holes === 0) return { cellCount: sharedWithBase, sharedWithBase, newGround: [] };
  if (holes < 0) return null;

  // A lyukak megkeresése és megszámolása az E melletti szabad cellákból,
  // először kis, aztán nagyobb korláttal (a külső kitöltése a drága rész).
  const solid = (cell: CellId) => added.has(cell) || inBase(cell);
  for (const cap of POCKET_SCAN_CAPS) {
    const found = findPockets(added, solid, cap, holes);
    if (found === null) return null;
    if (found.pockets === holes) {
      return { cellCount: sharedWithBase + found.cells.length, sharedWithBase, newGround: found.cells };
    }
  }
  return null;
}

export interface ShrunkInterior {
  /** A jelölt teljes belső cellaszáma. */
  cellCount: number;
  /** Ebből ennyi a `base` belsejében van (= a metszet). */
  sharedWithBase: number;
  /**
   * A `base`-en KÍVÜLI új belső cellák (a bekerített zsebek), pontosan.
   * Ugyanaz a halmaz, amit a `newGroundOutside(jelölt, base)` adna.
   */
  newGround: CellId[];
}

/**
 * A `candidate` belsejének az a része, ami a `record` falán és belsején KÍVÜL
 * esik — a teljes (felépített) úton. A gyors út ugyanezt a
 * `shrunkInteriorCellCount().newGround`-ban adja.
 *
 * Compact jelöltnél a record-ban már teljes parenteket átugorjuk; a többi
 * parentet kibontjuk. Csak akkor hívjuk, ha a két hurok amúgy „ugyanaz”
 * (`sameLoopGeometry`), tehát a különbség kicsi.
 */
export function newGroundOutside(candidate: DetectedLoop, record: DetectedLoop): Set<CellId> {
  const inRecord = (cell: CellId) => record.wall.has(cell) || loopInteriorHas(record, cell);
  const result = new Set<CellId>();
  for (const cell of candidate.interior) if (!inRecord(cell)) result.add(cell);
  const compact = candidate.compactInterior;
  if (compact) {
    const recordParents =
      record.compactInterior?.parentResolution === compact.parentResolution
        ? record.compactInterior.fullParents
        : undefined;
    for (const parent of compact.fullParents) {
      if (recordParents?.has(parent)) continue;
      for (const child of childrenOf(parent, GAMEPLAY.H3_RESOLUTION)) {
        if (!inRecord(child)) result.add(child);
      }
    }
  }
  return result;
}

/**
 * VALÓDI új terület-e: van-e benne cella, aminek MIND A HAT szomszédja is
 * benne van.
 *
 * Miért nem elég kevesebb: a „nyeles kör” hazaútján (ugyanazon az úton
 * vissza, a két oldal GPS-e 8–20 m-re) az út két oldala közé 1–2 cella széles
 * sáv szorul. Egy 2 széles sávban egy cellának 4 sávbeli szomszédja is lehet,
 * tehát a „≥ 3 szomszéd” (`hasTwoDimensionalInteriorCore`) itt átengedné, és
 * minden széles úton hazatérés bezárás lenne (03. fejezet 106. sor: épp ez
 * adott egy 11 km-es körnél 26 bezárást). A teljesen körülvett cella legalább
 * 3 cella széles zsebet jelent — az már egy másik út, nem az út másik oldala.
 */
export function hasSolidCore(cells: ReadonlySet<CellId>): boolean {
  for (const cell of cells) {
    let solid = true;
    for (const near of ringOf(cell)) {
      if (!cells.has(near)) {
        solid = false;
        break;
      }
    }
    if (solid) return true;
  }
  return false;
}

/**
 * Egy lyuk keresésének lépcsős felső korlátja cellában. Csak futásidő-védelem:
 * ennél nagyobb lyuknál a teljes felépítés fut, az eredmény nem változik. A
 * tipikus lyuk (két párhuzamos nyomvonal közti útsáv) néhány tucat cella.
 */
const POCKET_SCAN_CAPS = [64, 4_096] as const;

/**
 * Az E melletti szabad cellák komponensei. Véges, teljesen bejárt komponens =
 * lyuk. `cap` fölé nőtt, vagy egy ilyenhez érő komponens nagy (nem számoljuk).
 * `wanted` lyuk megtalálásakor megáll: annál több úgysincs. `null` = pentagon.
 */
function findPockets(
  added: ReadonlySet<CellId>,
  solid: (cell: CellId) => boolean,
  cap: number,
  wanted: number,
): { pockets: number; cells: CellId[] } | null {
  const LARGE = -1;
  const label = new Map<CellId, number>();
  let pockets = 0;
  const cells: CellId[] = [];
  let nextId = 0;
  for (const cell of added) {
    for (const seed of ringOf(cell)) {
      if (seed === cell || label.has(seed) || solid(seed)) continue;
      const id = nextId++;
      const component: CellId[] = [seed];
      label.set(seed, id);
      let large = false;
      scan: for (let at = 0; at < component.length; at += 1) {
        const ring = ringOf(component[at]!);
        if (ring.length !== 7) return null;
        for (const near of ring) {
          if (near === component[at] || solid(near)) continue;
          const seen = label.get(near);
          if (seen === LARGE) {
            large = true;
            break scan;
          }
          if (seen !== undefined) continue;
          label.set(near, id);
          component.push(near);
          if (component.length > cap) {
            large = true;
            break scan;
          }
        }
      }
      if (large) {
        for (const c of component) label.set(c, LARGE);
      } else {
        pockets += 1;
        for (const c of component) cells.push(c);
        if (pockets === wanted) return { pockets, cells };
      }
    }
  }
  return { pockets, cells };
}

interface BaseWallInfo {
  connected: boolean;
  bounds: Bounds;
  coarseBounds: Bounds;
}

/** Egy elfogadott hurok falára egyszer kiszámolt adatok (a fal Set-je nem változik). */
const baseWallInfoCache = new WeakMap<ReadonlySet<CellId>, BaseWallInfo>();

function baseWallInfo(wall: ReadonlySet<CellId>, parentResolution: number): BaseWallInfo {
  const cached = baseWallInfoCache.get(wall);
  if (cached) return cached;
  const coarseWall = new Set<CellId>();
  for (const cell of wall) coarseWall.add(parentOf(cell, parentResolution));
  const info = {
    connected: isConnected(wall),
    bounds: boundsOf(wall),
    coarseBounds: boundsOf(coarseWall),
  };
  baseWallInfoCache.set(wall, info);
  return info;
}

function unionBounds(a: Bounds, b: Bounds): Bounds {
  return {
    south: Math.min(a.south, b.south),
    north: Math.max(a.north, b.north),
    west: Math.min(a.west, b.west),
    east: Math.max(a.east, b.east),
  };
}

function isConnected(cells: ReadonlySet<CellId>): boolean {
  const first = cells.values().next().value as CellId | undefined;
  if (first === undefined) return true;
  const seen = new Set<CellId>([first]);
  const stack = [first];
  while (stack.length > 0) {
    for (const near of ringOf(stack.pop()!)) {
      if (cells.has(near) && !seen.has(near)) {
        seen.add(near);
        stack.push(near);
      }
    }
  }
  return seen.size === cells.size;
}

/* ════════════════════════════════════════════════════════════════════════
   Compact adaptív flood fill
   ════════════════════════════════════════════════════════════════════════ */

function buildCompactAdaptiveInterior(wall: ReadonlySet<CellId>): LoopInteriorGeometry {
  const first = wall.values().next().value as CellId | undefined;
  if (first === undefined) return { interior: new Set(), cellCount: 0 };

  const res = getResolution(first);
  const parentResolution = Math.max(0, res - COMPACT_PARENT_STEPS);

  /* 1. Durva menet. */
  const coarseWall = new Set<CellId>();
  for (const cell of wall) coarseWall.add(parentOf(cell, parentResolution));

  const coarseRegion = candidateRegion(coarseWall, parentResolution);
  const coarseOutside = new Set<CellId>();
  const coarseQueue: CellId[] = [];

  for (const cell of coarseRegion) {
    if (coarseWall.has(cell)) continue;
    for (const near of ringOf(cell)) {
      if (!coarseRegion.has(near)) {
        coarseOutside.add(cell);
        coarseQueue.push(cell);
        break;
      }
    }
  }
  spreadCoarse(coarseQueue);

  /* 2. Pontos menet csak a falat tartalmazó parentek res12 gyerekein. */
  const band = new Set<CellId>();
  for (const coarse of coarseWall) {
    for (const child of childrenOf(coarse, res)) band.add(child);
  }

  const fineOutside = new Set<CellId>();
  const fineQueue: CellId[] = [];
  for (const cell of band) {
    if (wall.has(cell)) continue;
    for (const near of ringOf(cell)) {
      if (band.has(near)) continue;
      const parent = parentOf(near, parentResolution);
      if (coarseOutside.has(parent) || !coarseRegion.has(parent)) {
        fineOutside.add(cell);
        fineQueue.push(cell);
        break;
      }
    }
  }
  spreadFine(fineQueue);

  /* 3. A finom menet által feltárt kijáratokat visszaterjesztjük durva szintre. */
  for (;;) {
    const opened: CellId[] = [];
    for (const cell of fineOutside) {
      for (const near of ringOf(cell)) {
        if (band.has(near) || wall.has(near)) continue;
        const parent = parentOf(near, parentResolution);
        if (coarseRegion.has(parent) && !coarseOutside.has(parent) && !coarseWall.has(parent)) {
          coarseOutside.add(parent);
          opened.push(parent);
        }
      }
    }
    if (opened.length === 0) break;
    spreadCoarse(opened);

    const again: CellId[] = [];
    for (const cell of band) {
      if (wall.has(cell) || fineOutside.has(cell)) continue;
      for (const near of ringOf(cell)) {
        if (band.has(near)) continue;
        if (coarseOutside.has(parentOf(near, parentResolution))) {
          fineOutside.add(cell);
          again.push(cell);
          break;
        }
      }
    }
    if (again.length === 0) break;
    spreadFine(again);
  }

  /* 4. Tömör eredmény: teljes parentek + pontos res12 határsáv. */
  const fullParents = new Set<CellId>();
  for (const coarse of coarseRegion) {
    if (!coarseOutside.has(coarse) && !coarseWall.has(coarse)) fullParents.add(coarse);
  }

  if (fullParents.size > MAX_COMPACT_PARENTS) {
    const approximateFineCells =
      fullParents.size * 7 ** COMPACT_PARENT_STEPS + band.size;
    throw new LoopTooLargeError(approximateFineCells);
  }

  const interior = new Set<CellId>();
  for (const cell of band) {
    if (!wall.has(cell) && !fineOutside.has(cell)) interior.add(cell);
  }

  let cellCount = interior.size;
  for (const parent of fullParents) {
    cellCount += Number(cellToChildrenSize(parent, res));
  }

  return {
    interior,
    compactInterior: {
      parentResolution,
      fullParents,
      cellCount,
    },
    cellCount,
  };

  function spreadCoarse(start: CellId[]): void {
    const stack = [...start];
    while (stack.length > 0) {
      const cell = stack.pop()!;
      for (const near of ringOf(cell)) {
        if (!coarseRegion.has(near) || coarseWall.has(near) || coarseOutside.has(near)) continue;
        coarseOutside.add(near);
        stack.push(near);
      }
    }
  }

  function spreadFine(start: CellId[]): void {
    const stack = [...start];
    while (stack.length > 0) {
      const cell = stack.pop()!;
      for (const near of ringOf(cell)) {
        if (!band.has(near) || wall.has(near) || fineOutside.has(near)) continue;
        fineOutside.add(near);
        stack.push(near);
      }
    }
  }
}

function candidateRegion(wall: ReadonlySet<CellId>, res: number): Set<CellId> {
  const bounds = boundsOf(wall);
  const estimated = estimateCellCount(bounds.south, bounds.north, bounds.west, bounds.east, res);
  if (estimated > GAMEPLAY.MAX_LOOP_BBOX_CELLS) throw new LoopTooLargeError(Math.round(estimated));

  const boundary: [number, number][] = [
    [bounds.south, bounds.west],
    [bounds.south, bounds.east],
    [bounds.north, bounds.east],
    [bounds.north, bounds.west],
  ];
  return new Set(polygonToCells([boundary], res));
}

function estimateRegionCells(wall: ReadonlySet<CellId>): number {
  const first = wall.values().next().value as CellId | undefined;
  if (first === undefined) return 0;
  const bounds = boundsOf(wall);
  return estimateCellCount(bounds.south, bounds.north, bounds.west, bounds.east, getResolution(first));
}

interface Bounds {
  south: number;
  north: number;
  west: number;
  east: number;
}

function boundsOf(wall: ReadonlySet<CellId>): Bounds {
  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;
  for (const cell of wall) {
    const [lat, lng] = cellToLatLng(cell);
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }

  const pad = 0.0006;
  return {
    south: minLat - pad,
    north: maxLat + pad,
    west: minLng - pad,
    east: maxLng + pad,
  };
}

function estimateCellCount(
  south: number,
  north: number,
  west: number,
  east: number,
  res: number,
): number {
  const M_PER_DEG = 111_320;
  const midLat = ((south + north) / 2) * (Math.PI / 180);
  const heightM = (north - south) * M_PER_DEG;
  const widthM = (east - west) * M_PER_DEG * Math.cos(midLat);
  return Math.abs(heightM * widthM) / cellAreaAt(res);
}

function cellAreaAt(res: number): number {
  return GAMEPLAY.CELL_AREA_M2 * 7 ** (GAMEPLAY.H3_RESOLUTION - res);
}
